import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { toast } from "sonner";
import {
  useAdminOfferLineTemplates,
  useUpsertOfferLineTemplate,
  useDeleteOfferLineTemplate,
} from "@/hooks/use-product-catalog";
import type { OfferLineTemplate } from "@/hooks/use-offers";
import type { OfferPhase } from "@/lib/offer-calc";
import { PHASE_OPTIONS, type Draft } from "./offer-line-templates/constants";
import { OfferLineTemplateRow } from "./offer-line-templates/OfferLineTemplateRow";
import { OfferLineTemplateNewRow } from "./offer-line-templates/OfferLineTemplateNewRow";

export function OfferLineTemplatesTable() {
  const { data: rows = [], isLoading } = useAdminOfferLineTemplates();
  const upsert = useUpsertOfferLineTemplate();
  const del = useDeleteOfferLineTemplate();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [newDraft, setNewDraft] = useState<Draft | null>(null);
  const [filter, setFilter] = useState<OfferPhase | "all">("all");

  const filtered = useMemo(
    () => (filter === "all" ? rows : rows.filter((r) => r.phase === filter)),
    [rows, filter],
  );

  function patchDraft(id: string, patch: Partial<OfferLineTemplate>) {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] ?? {}), ...patch } }));
  }

  function discardDraft(row: OfferLineTemplate) {
    setDrafts((d) => {
      const next = { ...d };
      delete next[row.id];
      return next;
    });
  }

  function getValue<K extends keyof OfferLineTemplate>(
    row: OfferLineTemplate,
    key: K,
  ): OfferLineTemplate[K] {
    const draft = drafts[row.id];
    return (draft && key in draft ? draft[key as keyof Draft] : row[key]) as OfferLineTemplate[K];
  }

  async function saveRow(row: OfferLineTemplate) {
    const draft = drafts[row.id];
    if (!draft) return;
    try {
      await upsert.mutateAsync({ id: row.id, ...draft });
      setDrafts((d) => {
        const next = { ...d };
        delete next[row.id];
        return next;
      });
      toast.success("Lagret");
    } catch (err) {
      toast.error(`Kunne ikke lagre: ${(err as Error).message}`);
    }
  }

  async function createNew() {
    if (!newDraft) return;
    if (!newDraft.name?.trim() || !newDraft.phase) {
      toast.error("Navn og fase er påkrevd");
      return;
    }
    try {
      await upsert.mutateAsync({
        name: newDraft.name.trim(),
        phase: newDraft.phase,
        category: newDraft.category ?? "general",
        cost_type: newDraft.cost_type ?? "internal",
        pricing_type: newDraft.pricing_type ?? "hourly",
        default_unit: newDraft.default_unit ?? "t",
        default_rate: newDraft.default_rate ?? null,
        default_quantity: newDraft.default_quantity ?? null,
        description: newDraft.description ?? null,
        sort_order: newDraft.sort_order ?? 999,
        is_active: true,
        is_medgatt: newDraft.is_medgatt ?? false,
        is_default_for_offers: newDraft.is_default_for_offers ?? false,
      });
      setNewDraft(null);
      toast.success("Linje lagt til");
    } catch (err) {
      toast.error(`Kunne ikke opprette: ${(err as Error).message}`);
    }
  }

  async function deleteRow(row: OfferLineTemplate) {
    if (!window.confirm(`Slett "${row.name}"?`)) return;
    try {
      await del.mutateAsync(row.id);
      toast.success("Slettet");
    } catch (err) {
      toast.error(`Kunne ikke slette: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Filtrer fase:</span>
          <Select value={filter} onValueChange={(v) => setFilter(v as OfferPhase | "all")}>
            <SelectTrigger className="h-8 w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle faser</SelectItem>
              {PHASE_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">{filtered.length} linjer</span>
        </div>
        <Button
          size="sm"
          onClick={() => setNewDraft({ phase: "pl", cost_type: "internal", pricing_type: "hourly", default_unit: "t" })}
          disabled={!!newDraft}
        >
          <SrIcon name="plus" className="w-3.5 h-3.5 mr-1" />
          Ny linje
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" style={{ minWidth: 1280 }}>
            <thead>
              <tr className="border-b border-border/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 px-2 font-medium min-w-[220px]">Navn</th>
                <th className="text-left py-2 px-2 font-medium w-32">Fase</th>
                <th className="text-left py-2 px-2 font-medium w-20">Kostn.</th>
                <th className="text-left py-2 px-2 font-medium w-20">Pris-type</th>
                <th className="text-left py-2 px-2 font-medium w-16">Enhet</th>
                <th className="text-right py-2 px-2 font-medium w-20">Sats</th>
                <th className="text-right py-2 px-2 font-medium w-16">Default qty</th>
                <th className="text-left py-2 px-2 font-medium min-w-[200px]">Beskrivelse</th>
                <th className="text-center py-2 px-2 font-medium w-16">Aktiv</th>
                <th className="text-center py-2 px-2 font-medium w-20">Medgått</th>
                <th className="text-center py-2 px-2 font-medium w-20">Auto-på-tilbud</th>
                <th className="text-right py-2 px-2 font-medium w-20">Sort</th>
                <th className="w-28"></th>
              </tr>
            </thead>
            <tbody>
              {newDraft && (
                <OfferLineTemplateNewRow
                  draft={newDraft}
                  onChange={(p) => setNewDraft({ ...newDraft, ...p })}
                  onSave={createNew}
                  onCancel={() => setNewDraft(null)}
                  saving={upsert.isPending}
                />
              )}
              {filtered.map((row) => (
                <OfferLineTemplateRow
                  key={row.id}
                  row={row}
                  dirty={!!drafts[row.id]}
                  getValue={getValue}
                  patchDraft={patchDraft}
                  onSave={saveRow}
                  onDiscard={discardDraft}
                  onDelete={deleteRow}
                  saving={upsert.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
